import { rem } from 'polished'
import React from 'react'
import styled from 'styled-components'
import {
  type LayoutProps,
  type FlexboxProps,
  type SpaceProps,
} from 'styled-system'
import { FavoriteMarker } from 'components/ExpertCard'
import Icon, { ICON_SIZE } from 'components/Icon'
import { Image, IMAGE_LAYOUT } from 'components/Image'
import { Flex } from 'components/Layout'
import { MarkdownRenderer } from 'components/MarkdownRenderer'
import { H3, Text } from 'components/Typography'
import {
  FONT_STACK,
  SPACE,
  RADIUS,
  FONT_SIZE,
  COLOR_INTENT,
  BORDER_WIDTH,
  Z_INDEX,
  FONT_WEIGHT,
} from 'Theme'
import { type Attachment } from 'types/graphql-generated'

const PROFILE_PHOTO_MAX_SIZE = rem(120)
const PROFILE_PHOTO_MINIMAL_SIZE_IN_PX = 120
const DEFAULT_ICON_SIZE = ICON_SIZE.PX_24

const Photo = styled(Image)`
  border-radius: ${RADIUS.CIRCLE};
  border: ${BORDER_WIDTH.PX_2} solid
    ${COLOR_INTENT.EXPERT_INFO.PROFILE_IMAGE_BORDER} !important; // override border none of the next image
`

interface ExpertInfoProps extends FlexboxProps, LayoutProps, SpaceProps {
  name: string
  description?: string
  socialLinks: {
    youtube?: string
    twitter?: string
    instagram?: string
    facebook?: string
    linkedin?: string
  }
  location?: string
  isFavorite: boolean
  onRemoveFromFavorite: () => void
  onAddToFavorite: () => void
  isAddToFavoriteLoading: boolean
  onNotAuthorizedClick: () => void
  additionalContent?: React.ReactNode
  imageAttachment: Pick<Attachment, 'url' | 'alt'>
  isMobile: boolean
}

export const ExpertInfo: React.FC<React.PWC<ExpertInfoProps>> = ({
  name,
  description,
  socialLinks,
  location,
  isFavorite,
  onRemoveFromFavorite,
  onAddToFavorite,
  isAddToFavoriteLoading,
  onNotAuthorizedClick,
  additionalContent,
  imageAttachment,
  isMobile,
  ...rest
}) => (
  <Flex
    flexDirection="column"
    justifyContent="end"
    position="relative"
    flex={1}
    pb={SPACE.PX_5}
    px={{ TABLET: SPACE.PX_16 }}
    {...rest}
  >
    <Flex
      flexDirection="column"
      maxHeight={PROFILE_PHOTO_MAX_SIZE}
      maxWidth={PROFILE_PHOTO_MAX_SIZE}
      height={PROFILE_PHOTO_MAX_SIZE}
      width={PROFILE_PHOTO_MAX_SIZE}
      {...(isMobile
        ? {
            // setup for profile image overlay over carousel
            zIndex: Z_INDEX.MOBILE_EXPERT_PROFILE_IMAGE,
            position: 'absolute',
            top: `calc(${PROFILE_PHOTO_MAX_SIZE} * -0.6)`, // move 3/5 upward to partially overlay the component above
            left: SPACE.PX_15,
          }
        : {})}
    >
      <Photo
        layout={IMAGE_LAYOUT.RESPONSIVE}
        alt={imageAttachment?.alt || "Expert's picture"}
        src={imageAttachment?.url}
        sizes={`
            (max-width: 768px) ${PROFILE_PHOTO_MINIMAL_SIZE_IN_PX}px, 33vw`}
      />
    </Flex>
    <Flex
      mt={{ MOBILE: SPACE.PX_80, TABLET: SPACE.PX_20 }}
      flexDirection="column"
      alignItems={{ MOBILE: 'center', TABLET: 'start' }}
    >
      <H3
        as="h1"
        fontWeight={FONT_WEIGHT.NORMAL}
        fontFamily={FONT_STACK.SERIF}
        fontSize={FONT_SIZE.PX_32}
        mb={SPACE.PX_5}
      >
        {name}
      </H3>
      <Flex my={SPACE.PX_8} alignItems="center">
        {location && (
          <>
            <Icon.PinOutlined
              size={ICON_SIZE.PX_20}
              color={COLOR_INTENT.EXPERT_INFO.PIN_ICON}
            />
            <Text
              fontSize={FONT_SIZE.PX_13}
              fontFamily={FONT_STACK.SERIF}
              mt={SPACE.PX_5}
              ml={SPACE.PX_5}
            >
              {location}
            </Text>
          </>
        )}
        <Flex>
          {socialLinks?.instagram && (
            <Flex
              ml={SPACE.PX_8}
              alignItems="center"
              justifyContent="center"
              width={SPACE.PX_40}
              height={SPACE.PX_40}
            >
              <a
                href={socialLinks.instagram}
                target="_blank"
                rel="noreferrer"
                aria-label={`Link to Instagram of ${name}`}
              >
                <Icon.Instagram size={ICON_SIZE.PX_22} />
              </a>
            </Flex>
          )}
          <FavoriteMarker
            color={COLOR_INTENT.EXPERT_INFO.FAVORITE_MARKER}
            isLoading={isAddToFavoriteLoading}
            isFavorite={isFavorite}
            onRemoveFromFavorite={onRemoveFromFavorite}
            onAddToFavorite={onAddToFavorite}
            onNotAuthorizedClick={onNotAuthorizedClick}
            size={DEFAULT_ICON_SIZE}
          />
        </Flex>
      </Flex>
      <MarkdownRenderer
        fontSize={FONT_SIZE.PX_16}
        fontFamily={FONT_STACK.SERIF}
        textAlign={{ MOBILE: 'center', TABLET: 'start' }}
        mb={SPACE.PX_20}
        markdownSource={description}
      />
      {additionalContent}
    </Flex>
  </Flex>
)
